import {Component, ComponentConstructor, Object3D, Type} from '@wonderlandengine/api';

/**
 * The normal GetComponents does not work well with inheritance. This function
 * does.
 * @param object The object to get the components from.
 * @param type The type of component to get.
 * @returns An array of components of the given type.
 */
function getComponentsOfType<T extends Component>(object: Object3D, type: ComponentConstructor<T>): T[] {
    return object.getComponents().filter((c) => c instanceof type) as T[];
}

function getComponentOfType<T extends Component>(object: Object3D, type: ComponentConstructor<T>): T {
    return getComponentsOfType(object, type)[0];
}
/**
 * Recursively sets the active state of the given object and all its children.
 * @param object The object to set the active state of.
 * @param active The state to set.
 */
function setActive(object: Object3D, active: boolean) {
    object.active = active;
    object.getComponents().forEach((c) => (c.active = active));
    object.children.forEach((c) => setActive(c, active));
}

function traverse(source: Object3D, cb: (o: Object3D) => void) {
    const stack: Object3D[] = [source];
    let i = 0;

    while (i < stack.length) {
        const obj = stack[i++];
        if (!obj) continue;

        cb(obj);
        stack.push.apply(stack, obj.children);
    }

    return stack;
}

function objects(source: Object3D): Object3D[] {
    return traverse(source, () => {});
}

function components(source: Object3D): Component[] {
    const result: Component[] = [];
    traverse(source, (obj: Object3D) => {
        const comps = obj.getComponents();
        result.push.apply(result, comps);
    });
    return result;
}

function destroyWithDelay(object: Object3D, delay: number) {
    setTimeout(() => {
        if (object) {
            // ignore if there's no object anymore.
            if (object.isDestroyed) {
                // ignore if the object is already destroyed.
                return;
            }
            object.destroy();
        }
    }, delay);
}

export const WLUtils = {
    setActive,
    destroyWithDelay,
    getComponentsOfType,
    getComponentOfType,
};
